import { getProjectSetting } from '@service/apis/projectInfo.api';
import { postProjectSetting } from '@service/apis/projects.api';
import {
  applyPendingOverridesToModuleInfo,
  buildProjectSettingsSavePayload,
} from './buildProjectSettingsSavePayload';
import { getModuleEnableChat, getModuleRowValue } from './moduleRowFields';
import {
  getInstanceTypeFromProjectSettings,
  normalizeProjectSettingsResponse,
} from './normalizeProjectSettingsResponse';

export const CHAT_AI_ENABLE_CHAT_SERVER_CHANGED =
  'ddv-ai-chat-enable-chat-server-changed';

/** Resolves `enableChat` from GET map when UI `value` casing differs from API. */
export function lookupEnableChatInServerMap(serverModuleMap, valueStr) {
  if (!serverModuleMap || valueStr == null) {
    return undefined;
  }
  const v = String(valueStr);
  if (typeof serverModuleMap[v] === 'boolean') {
    return serverModuleMap[v];
  }
  const hit = Object.entries(serverModuleMap).find(
    ([k]) => k.toLowerCase() === v.toLowerCase(),
  );
  return hit ? hit[1] : undefined;
}

/**
 * Builds value → enableChat for gating. When the API omits `enableChat`, treat as
 * enabled so legacy payloads do not hide chat; explicit false still disables.
 */
export function extractEnableChatMapFromModuleInfo(moduleInfo) {
  const map = {};
  if (!Array.isArray(moduleInfo)) {
    return map;
  }
  for (const m of moduleInfo) {
    const v = getModuleRowValue(m);
    if (v == null) {
      continue;
    }
    const c = getModuleEnableChat(m);
    map[String(v)] = typeof c === 'boolean' ? c : true;
  }
  return map;
}

/**
 * @param {string} projectId
 * @returns {Promise<{ projectData: object, moduleEnableChatMap: Record<string, boolean> } | null>}
 */
export async function fetchProjectSettingsForEnableChat(projectId) {
  if (!projectId) {
    return null;
  }
  const raw = await getProjectSetting(projectId);
  if (!raw) {
    return null;
  }
  const instanceType = getInstanceTypeFromProjectSettings(raw);
  const projectData = normalizeProjectSettingsResponse(raw, instanceType);
  if (!projectData) {
    return null;
  }
  const moduleEnableChatMap = extractEnableChatMapFromModuleInfo(
    projectData.moduleInfo,
  );
  return { projectData, moduleEnableChatMap };
}

/**
 * POST full project settings with `enableChat` set on rows the user edited.
 *
 * @param {string} projectId
 * @param {{
 *   pendingOverrides: Record<string, boolean>,
 * }} draft — checkbox draft keyed by module row id or `value` (same strings as UI)
 * @param {string} operatorName — `teamSetting.operatorName`
 * @returns {Promise<{ success: boolean, hasAuth?: boolean, error?: string }>}
 */
export async function saveModuleEnableChatFlags(
  projectId,
  draft,
  operatorName,
) {
  if (!projectId || !operatorName) {
    return { success: false, error: 'Missing project or operator.' };
  }

  try {
    const fetched = await fetchProjectSettingsForEnableChat(projectId);
    if (!fetched?.projectData?.moduleInfo) {
      return { success: false, error: 'Could not load project settings.' };
    }

    const { projectData } = fetched;
    const pendingOverrides =
      draft?.pendingOverrides && typeof draft.pendingOverrides === 'object'
        ? draft.pendingOverrides
        : {};
    const mergedModules = applyPendingOverridesToModuleInfo(
      projectData.moduleInfo,
      pendingOverrides,
    );
    const projectDataMerged = { ...projectData, moduleInfo: mergedModules };

    const payload = buildProjectSettingsSavePayload(
      projectDataMerged,
      operatorName,
    );

    const result = await postProjectSetting(projectId, payload);
    if (!result) {
      return { success: false, error: 'Save failed.' };
    }

    const moduleEnableChatMap =
      extractEnableChatMapFromModuleInfo(mergedModules);
    window.dispatchEvent(
      new CustomEvent(CHAT_AI_ENABLE_CHAT_SERVER_CHANGED, {
        detail: { projectId, moduleEnableChatMap },
      }),
    );

    return {
      success: true,
      hasAuth: Boolean(result.currentUserMembership),
    };
  } catch (e) {
    const msg = e?.message || 'Could not save module chat settings.';
    return {
      success: false,
      error: typeof msg === 'string' ? msg : String(msg),
    };
  }
}
